// shared-lib/millwork/stickPacking.js
/**
 * How many stock-length sticks a pool of millwork pieces is cut from
 * (spec §3). Every piece costs its length plus the cut allowance. Pieces go
 * longest first, each into the first stick with room, a new stick only when
 * none has (first-fit decreasing) — it runs the same way every time. A
 * JOINABLE run (baseboard, crown) longer than a stick takes whole sticks and
 * its remainder is packed; a casing piece is never joined, so one longer than
 * the stick is a stick of its own and is counted in `overLong`. Waste is a
 * percentage on top, rounded up.
 *
 * Pure.
 */
const IN_PER_M = 39.37007874;
const EPS = 1e-6;

const packSticks = (pieces, { stickFt, cutAllowanceIn, wastePct = 0 }) => {
  const stickIn = stickFt * 12;
  const allowance = cutAllowanceIn;
  if (!(stickIn > allowance)) {
    throw new Error(
      `packSticks: a ${stickFt} ft stick is no longer than the ${allowance}" cut allowance`
    );
  }
  let fullSticks = 0;
  let overLong = 0;
  let lengthM = 0;
  let pieceCount = 0;
  const items = [];
  for (const piece of pieces || []) {
    const lenIn = (piece?.lenM || 0) * IN_PER_M;
    if (!(lenIn > EPS)) continue;
    pieceCount += 1;
    lengthM += piece.lenM;
    if (lenIn + allowance <= stickIn + EPS) {
      items.push(lenIn + allowance);
    } else if (piece.joinable) {
      let rest = lenIn;
      while (rest + allowance > stickIn + EPS) {
        fullSticks += 1;
        rest -= stickIn - allowance;
      }
      if (rest > EPS) items.push(rest + allowance);
    } else {
      overLong += 1;
    }
  }
  items.sort((a, b) => b - a);
  const room = [];
  for (const size of items) {
    const i = room.findIndex((left) => left + EPS >= size);
    if (i === -1) room.push(stickIn - size);
    else room[i] -= size;
  }
  const packedSticks = fullSticks + overLong + room.length;
  return {
    // Math.max keeps an empty pool at +0 (Math.ceil of a tiny negative is -0).
    sticks: Math.max(0, Math.ceil(packedSticks * (1 + wastePct) - EPS)),
    packedSticks,
    lengthM,
    pieceCount,
    overLong,
  };
};

module.exports = { packSticks };
